import React from 'react';
import { Trash2, Smartphone, Mail } from 'lucide-react';

const S: Record<string, React.CSSProperties> = {
  page: {
    fontFamily: "'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif",
    background: '#F8FAFC',
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    WebkitFontSmoothing: 'antialiased',
  },
  header: {
    height: 68,
    display: 'flex',
    alignItems: 'center',
    borderBottom: '1px solid #E2E8F0',
    background: '#fff',
    padding: '0 28px',
  },
  logoMark: {
    width: 38,
    height: 38,
    background: '#FEF2F2',
    borderRadius: 11,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  logoInner: {
    width: 24,
    height: 24,
    background: 'linear-gradient(135deg,#EF4444 0%,#DC2626 100%)',
    borderRadius: 6,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 2px 8px rgba(239,68,68,.35)',
  },
  logoTxt: {
    fontWeight: 800,
    fontSize: 17,
    color: '#0F172A',
    letterSpacing: '-0.025em',
    marginLeft: 10,
  },
  main: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px 24px',
  },
  card: {
    background: '#fff',
    borderRadius: 32,
    padding: '56px 48px',
    width: '100%',
    maxWidth: 480,
    boxShadow: '0 4px 16px -4px rgba(15,23,42,.08), 0 1px 2px rgba(15,23,42,.04)',
    textAlign: 'center',
  },
  iconCircle: {
    width: 88,
    height: 88,
    borderRadius: '50%',
    margin: '0 auto 32px',
    background: '#FEF2F2',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {
    fontSize: 26,
    fontWeight: 900,
    letterSpacing: '-0.035em',
    color: '#0F172A',
    marginBottom: 12,
  },
  desc: {
    fontSize: 15,
    color: '#64748B',
    lineHeight: 1.65,
    marginBottom: 36,
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    margin: '24px 0',
  },
  dividerLine: {
    flex: 1,
    height: 1,
    background: '#E2E8F0',
  },
  dividerTxt: {
    fontSize: 12,
    color: '#94A3B8',
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.06em',
  },
  optionCard: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 16,
    background: '#F8FAFC',
    borderRadius: 20,
    padding: '20px 20px',
    marginBottom: 12,
    textAlign: 'left' as const,
    border: '1.5px solid #E2E8F0',
  },
  optionIcon: {
    width: 40,
    height: 40,
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#EEF2FF',
    flexShrink: 0,
  },
  optionTitle: {
    fontSize: 15,
    fontWeight: 700,
    color: '#0F172A',
    marginBottom: 4,
  },
  optionDesc: {
    fontSize: 13,
    color: '#64748B',
    lineHeight: 1.5,
    marginBottom: 12,
  },
  btnPrimary: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    width: '100%',
    height: 48,
    border: 'none',
    borderRadius: 14,
    background: 'linear-gradient(135deg,#EF4444 0%,#DC2626 100%)',
    color: '#fff',
    fontWeight: 700,
    fontSize: 15,
    cursor: 'pointer',
    boxShadow: '0 6px 16px rgba(239,68,68,.25)',
    textDecoration: 'none',
    transition: 'opacity .15s, transform .12s',
    fontFamily: 'inherit',
  },
  btnGhost: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    width: '100%',
    height: 48,
    border: '1.5px solid #E2E8F0',
    borderRadius: 14,
    background: '#fff',
    color: '#334155',
    fontWeight: 600,
    fontSize: 15,
    cursor: 'pointer',
    textDecoration: 'none',
    transition: 'border-color .15s, background .15s',
    fontFamily: 'inherit',
  },
  notice: {
    fontSize: 12,
    color: '#94A3B8',
    lineHeight: 1.6,
    marginTop: 28,
    padding: '16px',
    background: '#F8FAFC',
    borderRadius: 12,
    textAlign: 'left' as const,
    border: '1px solid #E2E8F0',
  },
  footer: {
    padding: '24px',
    textAlign: 'center',
    fontSize: 12,
    color: '#94A3B8',
  },
  footerLink: {
    color: '#64748B',
    textDecoration: 'none',
    fontWeight: 500,
  },
};

const SUPPORT_EMAIL = 'support@getspeakhub.com';
const DEEP_LINK = 'getspeakhub://profile';

const DeleteAccountPage: React.FC = () => {
  const handleOpenApp = () => {
    window.location.href = DEEP_LINK;
  };

  const mailtoHref =
    `mailto:${SUPPORT_EMAIL}` +
    `?subject=${encodeURIComponent('Account Deletion Request')}` +
    `&body=${encodeURIComponent(
      'Hello,\n\nI would like to permanently delete my GetSpeakHub account and all associated data.\n\nRegistered email: \nUser ID (if known): \n\nThank you.'
    )}`;

  return (
    <>
      <style>{`
        @keyframes fadeUp { from { opacity:0; transform:translateY(20px); } to { opacity:1; transform:translateY(0); } }
        .da-card { animation: fadeUp .65s cubic-bezier(.22,1,.36,1) both; }
        .da-btn-primary:hover { opacity:.92; transform:translateY(-1px); }
        .da-btn-ghost:hover { border-color:#CBD5E1 !important; background:#F8FAFC !important; }
      `}</style>

      <div style={S.page}>
        <header style={S.header}>
          <a href="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
            <div style={S.logoMark}>
              <div style={S.logoInner}>
                <span style={{ color: '#fff', fontWeight: 900, fontSize: 13 }}>G</span>
              </div>
            </div>
            <span style={S.logoTxt}>GetSpeakHub</span>
          </a>
        </header>

        <main style={S.main}>
          <div style={S.card} className="da-card">
            <div style={S.iconCircle}>
              <Trash2 size={40} color="#EF4444" strokeWidth={1.8} />
            </div>

            <h1 style={S.title}>Delete Your Account</h1>
            <p style={S.desc}>
              You can permanently delete your account and all associated data using either option below.
              This action is <strong>irreversible</strong>.
            </p>

            <div style={S.optionCard}>
              <div style={S.optionIcon}>
                <Smartphone size={20} color="#6366F1" strokeWidth={2} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={S.optionTitle}>Delete from the app</div>
                <div style={S.optionDesc}>
                  Open the app → Profile tab → scroll to <strong>Account</strong> section → tap <strong>Delete Account</strong>.
                </div>
                <button
                  style={S.btnPrimary}
                  className="da-btn-primary"
                  onClick={handleOpenApp}
                >
                  Open GetSpeakHub App
                </button>
              </div>
            </div>

            <div style={S.divider}>
              <div style={S.dividerLine} />
              <span style={S.dividerTxt}>or</span>
              <div style={S.dividerLine} />
            </div>

            <div style={S.optionCard}>
              <div style={{ ...S.optionIcon, background: '#F0FDF4' }}>
                <Mail size={20} color="#16A34A" strokeWidth={2} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={S.optionTitle}>Request by email</div>
                <div style={S.optionDesc}>
                  If you no longer have access to the app, email us from your registered address.
                  We will process your request within 30 days.
                </div>
                <a
                  href={mailtoHref}
                  style={S.btnGhost}
                  className="da-btn-ghost"
                >
                  <Mail size={16} />
                  Email Deletion Request
                </a>
              </div>
            </div>

            <div style={S.notice}>
              <strong style={{ color: '#475569' }}>What gets deleted:</strong> your profile, photos, messages, event
              history, match data, and all personal information. Anonymised aggregate statistics
              may be retained for platform integrity purposes as described in our{' '}
              <a href="/privacy" style={{ color: '#6366F1', fontWeight: 600 }}>Privacy Policy</a>.
            </div>
          </div>
        </main>

        <footer style={S.footer}>
          <a href="/terms" style={S.footerLink}>Terms of Service</a>
          {' · '}
          <a href="/privacy" style={S.footerLink}>Privacy Policy</a>
          {' · '}
          © 2026 GetSpeakHub, Inc.
        </footer>
      </div>
    </>
  );
};

export default DeleteAccountPage;
